import * as React from 'react';
import { ECommentListDirection, ICommentsListProps } from './types';
import { CLASS_COMMENT, CLASS_COMMENTS } from '../../constants';
import './_CommentsList.scss';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { TCommentId } from '../../../../../../../store/model/card/types/IComment';
import { CommentHOC } from '../../hocs/CommentHOC/CommentHOC';
import { COMMENT_ANIMATION_DURATION } from '../Comment/constants';
import { CommentInputHOC } from '../../hocs/CommentsInputHOC/CommentsInputHOC';
import { COMMENT_REPLY_ANIMATION_DURATION, DEFAULT_INPUT_HEIGHT } from '../CommentsInput/constants';
import { GOOGLE_SPACING } from '../../../../../../../const';
import { Loader } from 'kui';

//@ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CommentsList`) : null;

export function CommentsList({
    ascOrder,
    boardId,
    cardId,
    comments,
    commentCount,
    direction = ECommentListDirection.UP,
    isChatView,
    isReadonly,
    lastCommentSeenTime: _lastCommentSeenTime,
    openedProperty,
    sectionType,
    onClose,
    onEdit,
    onLoad,
    onResetUnseen,
}: ICommentsListProps) {
    if (analyzeRender) analyzeRender.call(`CommentsList ${cardId}`);

    const className = CLASS_COMMENTS + '-list';

    const [inputHeight, setInputHeight] = React.useState(isReadonly ? 0 : DEFAULT_INPUT_HEIGHT);
    const [activeCommentId, setActiveCommentId] = React.useState(null);
    const [scrollToCommentId, setScrollToCommentId] = React.useState(null);

    const listRef = React.useRef(null);

    const scrollToComment = (commentId: TCommentId) => {
        const comment = document.querySelector(`.${CLASS_COMMENT}--${commentId}`) as HTMLElement;
        if (!comment) return;

        if (
            !comments.find(comment => comment.id === commentId) &&
            !ascOrder
        ) { // новый комментарий скролим максимально вниз, чтобы был выше инпута
            const list = listRef.current as HTMLElement;
            list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
        } else {
            comment.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        setActiveCommentId(commentId);
        setScrollToCommentId(null);
    };

    const onLoadMore = () => {
        if (commentCount > comments.length) {
            onLoad().then(() => {
                if (scrollToCommentId) {
                    scrollToComment(scrollToCommentId);
                }
            });
        }
    };

    React.useEffect(() => {
        if (!activeCommentId) return;
        const comment = document.querySelector(`.${CLASS_COMMENT}--${activeCommentId}`);
        if (!comment) return;
        setActiveCommentId(null);
        const className = CLASS_COMMENT + '--active';
        comment.classList.add(className);
        setTimeout(() => {
            comment.classList.remove(className);
        }, COMMENT_ANIMATION_DURATION);
    }, [activeCommentId]);

    const [isScrolled, setScrolled] = React.useState(direction === ECommentListDirection.DOWN || !comments.length);
    React.useEffect(() => {
        if (!comments.length || direction === ECommentListDirection.DOWN) return;
        setScrolled(false);
    }, [cardId, isReadonly]);

    const [lastCommentSeenTime, setLastCommentSeenTime] = React.useState(_lastCommentSeenTime);
    React.useEffect(() => {
        setLastCommentSeenTime(_lastCommentSeenTime);
    }, [cardId]);

    const [dividerCommentId, setDividerCommentId] = React.useState(null);
    React.useEffect(() => {
        if (!lastCommentSeenTime || !comments.length) return;
        const comment = comments.find(comment => comment.created > lastCommentSeenTime);
        if (comment) {
            setDividerCommentId(comment.id);
        }
    }, [comments, lastCommentSeenTime]);

    const [newCommentId, setNewCommentId] = React.useState(null);
    React.useEffect(() => {
        if (!comments.length || !onResetUnseen) return;
        const comment = comments.find(comment => comment.created > _lastCommentSeenTime);
        setNewCommentId(comment && comment.id);
    }, [comments, _lastCommentSeenTime]);

    React.useEffect(() => {
        if (!newCommentId || !onResetUnseen) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    onResetUnseen();
                }
            },
            {
                root: listRef.current,
                threshold: 0.1,
            }
        );

        const comment = document.querySelector(`.${CLASS_COMMENT}--${newCommentId}`) as HTMLElement;
        if (comment) {
            observer.observe(comment);
        }

        return () => {
            const comment = document.querySelector(`.${CLASS_COMMENT}--${newCommentId}`) as HTMLElement;
            if (comment) {
                observer.unobserve(comment);
            }
        };
    }, [newCommentId, inputHeight]);

    const timerRef = React.useRef(null);
    React.useEffect(() => {
        if (
            isScrolled ||
            !comments.length ||
            !listRef.current ||
            direction === ECommentListDirection.DOWN
        ) {
            return;
        }

        const handleImageLoad = (img: HTMLImageElement) => {
            const list = listRef.current;
            if (!list) return;

            const scrollFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;

            setTimeout(() => {
                const newScrollHeight = list.scrollHeight;
                const newScrollFromBottom = newScrollHeight - list.scrollTop - list.clientHeight;
                if (scrollFromBottom <= 0) {
                    list.scrollTop = newScrollHeight - list.clientHeight;
                } else if (newScrollFromBottom <= 0) {
                    list.scrollTop = newScrollHeight - list.clientHeight;
                }
            }, 0);
        };
        document.querySelectorAll(`.${className + '__items'} img`).forEach((img: HTMLImageElement) => {
            img.addEventListener('load', () => handleImageLoad(img));
        });
        const onScrollToBottom = () => {
            const list = listRef.current
            if (!list) {
                timerRef.current = setTimeout(onScrollToBottom, 50);
                return;
            }
            setTimeout(() => { // подождать пока у комментариев появится текст
                list.scrollTop = list.scrollHeight;
                setScrolled(true);
            }, 0);
        };
        timerRef.current = setTimeout(onScrollToBottom, 0);
    }, [comments, listRef.current, isScrolled]);

    const loader = <div className={className + '__loader'}><Loader /></div>;

    return <div className={`${className} ${isScrolled ? className + '--visible' : ''}`}>
        {!!comments.length &&
            <div
                className={className + '__items'}
                ref={listRef}
                style={{ '--comments-input-height': `${inputHeight}px` } as React.CSSProperties}
            >
                {comments.map((comment, index) => (
                    <CommentHOC
                        key={comment.id}
                        isChatView={isChatView}
                        boardId={boardId}
                        comment={comment}
                        scrollToComment={scrollToComment}
                        isSameUserNextComment={comments[index + 1] ? comments[index + 1].author.permissionId === comment.author.permissionId : false}
                        isSameUserPrevComment={comments[index - 1] ? comments[index - 1].author.permissionId === comment.author.permissionId : false}
                        isNewDivider={dividerCommentId === comment.id}
                        onEdit={onEdit}
                    />
                ))}
            </div>
        }
        {!isReadonly &&
            <CommentInputHOC
                boardId={boardId}
                cardId={cardId}
                openedProperty={openedProperty}
                sectionType={sectionType}
                onClose={onClose}
                setInputHeight={setInputHeight}
                onScrollToComment={scrollToComment}
                onScrollTop={offset => {
                    if (!comments.length) return;
                    setTimeout(() => {
                        const list = listRef.current as HTMLElement;
                        if (!list) return;
                        const { scrollTop, scrollHeight, offsetHeight } = list;
                        if (scrollTop < scrollHeight - offsetHeight - GOOGLE_SPACING * 1.5) {
                            listRef.current.scrollTo({ top: scrollTop + offset, behavior: 'smooth' });
                        }
                    }, COMMENT_REPLY_ANIMATION_DURATION);
                }}
            />
        }
    </div>;
}
